import React, { useEffect } from "react";
import { MapPin, Phone, Clock, ArrowRight, Car, Footprints } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import {
    ADDRESS,
    ADDRESS_FULL,
    MAPS_EMBED_URL,
    MAPS_LINK,
    PHONE,
    PHONE_TEL,
} from "../data/constants";

const MapsPage = () => {
    const { t } = useLang();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div data-testid="maps-page" className="pt-32 pb-24 bg-ivory min-h-screen">
            <section className="container-x mb-12 text-center">
                <p className="eyebrow">{t("location.eyebrow")}</p>
                <h1 className="headline text-aegean-700 text-6xl md:text-7xl lg:text-8xl mt-3">
                    {t("maps.title")}
                </h1>
                <p className="mt-5 text-ink/70 max-w-2xl mx-auto">{t("maps.subtitle")}</p>
            </section>

            <section className="container-x">
                <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 aspect-[16/11] rounded-sm overflow-hidden shadow-2xl">
                        <iframe
                            title="AMOS location"
                            data-testid="maps-iframe"
                            src={MAPS_EMBED_URL}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>

                    <aside className="bg-aegean-700 text-white p-8 md:p-10 flex flex-col">
                        <h3 className="font-display uppercase tracking-[0.28em] text-xs text-aegean-200 mb-6">
                            {t("maps.info")}
                        </h3>

                        <div className="space-y-6 flex-1">
                            <div className="flex items-start gap-3">
                                <MapPin size={20} className="text-aegean-300 mt-1 shrink-0" />
                                <div>
                                    <div className="text-sm text-white/60 mb-1">Address</div>
                                    <div className="text-white font-medium">{ADDRESS_FULL}</div>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <Phone size={20} className="text-aegean-300 mt-1 shrink-0" />
                                <div>
                                    <div className="text-sm text-white/60 mb-1">{t("nav.callUs")}</div>
                                    <a href={`tel:${PHONE_TEL}`} className="text-white font-medium hover:text-aegean-300">
                                        {PHONE}
                                    </a>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <Clock size={20} className="text-aegean-300 mt-1 shrink-0" />
                                <div>
                                    <div className="text-sm text-white/60 mb-1">Hours</div>
                                    <div className="text-white font-medium">{t("location.hours")}</div>
                                </div>
                            </div>
                        </div>

                        <a
                            href={MAPS_LINK}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="open-google-maps-btn"
                            className="mt-8 btn-pill bg-white text-aegean-700 hover:bg-aegean-300 hover:text-white w-full"
                        >
                            {t("location.cta")} <ArrowRight size={16} />
                        </a>
                    </aside>
                </div>
            </section>

            {/* How to find us */}
            <section className="container-x mt-20 grid md:grid-cols-2 gap-8">
                <div className="bg-white p-8 md:p-10 rounded-sm shadow-lg border border-aegean-100">
                    <Car className="text-aegean-500" size={32} />
                    <h4 className="mt-5 font-display uppercase tracking-[0.18em] text-xl text-aegean-800">
                        {t("maps.byCar")}
                    </h4>
                    <p className="mt-3 text-ink/70">{t("maps.byCarBody")}</p>
                </div>
                <div className="bg-white p-8 md:p-10 rounded-sm shadow-lg border border-aegean-100">
                    <Footprints className="text-aegean-500" size={32} />
                    <h4 className="mt-5 font-display uppercase tracking-[0.18em] text-xl text-aegean-800">
                        {t("maps.byFoot")}
                    </h4>
                    <p className="mt-3 text-ink/70">{t("maps.byFootBody")}</p>
                </div>
            </section>
        </div>
    );
};

export default MapsPage;
